import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import classNames from 'classnames';
import { refreshPipeline } from 'js/actions';
import { splitDataSet } from 'js/actions/dataSets';
import { splitterReset } from 'js/actions/splitter';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { ROUTE_MY_NEXSETS } from 'new/constants/pathnames';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classes from './DataSetSplitDialog.module.scss';
import ExpressionsEditor, { ExpressionItem } from './ExpressionsEditor';
import ValuesEditor, { ValueItem } from './ValuesEditor';
import useSplitDataSet from './useSplitDataSet';

export const MODE_EXPRESSIONS = 'expression';
export const MODE_VALUES = 'values';
export const FALLBACK_DISCARD = 'fallback_discard';
export const FALLBACK_NEW_NEXSET = 'fallback_new_nexset';

interface Props {
  parentDataSet?: any;
  splitterDataSet?: any;
  onClose: () => void;
}

export default function DataSetSplitDialog({ parentDataSet, splitterDataSet, onClose }: Props) {
  const dispatch = useAppDispatch();
  const {
    mode,
    expressions,
    attr,
    values,
    fallback,
    parentDataSetSchema,
    isLoading,
    setMode,
    setExpressions,
    setAttr,
    setValues,
    setFallback,
    fetchDataSets,
    validateExpressions,
  } = useSplitDataSet(parentDataSet, splitterDataSet);
  const [parsingErrors, setParsingErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // when the dialog is opened, fetch the expanded data sets
    fetchDataSets();

    return () => {
      // when the dialog is closed, clear the state
      dispatch(splitterReset());
    };
  }, []);

  const onExpressionsChanged = (expressions: ExpressionItem[]) => {
    setExpressions(expressions);
    setParsingErrors({});
  };

  const onSubmit = () => {
    const errors = validateExpressions();

    if (errors) {
      setParsingErrors(errors);
      return;
    }

    setIsSubmitting(true);

    dispatch(
      splitDataSet(
        parentDataSet.id,
        splitterDataSet?.id,
        mode,
        expressions,
        attr,
        values,
        fallback,
      ),
    )
      .then(() => {
        if (parentDataSet.origin_node_id) {
          dispatch(refreshPipeline(parentDataSet.origin_node_id));
        }

        onClose();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Dialog className={classes.dialog} maxWidth="md" fullWidth open onClose={onClose}>
      <DialogTitle className={classes.header}>
        Splitting Rules
        <div>
          <Link
            className={classes.testInNexsetDesigner}
            to={
              splitterDataSet
                ? `${ROUTE_MY_NEXSETS}/${splitterDataSet.id}/edit`
                : `${ROUTE_MY_NEXSETS}/${parentDataSet.id}/split`
            }
          >
            Test in Nexset Designer
          </Link>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <DataSetSplitModes
          mode={mode}
          parentDataSetSchema={parentDataSetSchema}
          parsingErrors={parsingErrors}
          expressions={expressions}
          attr={attr}
          values={values}
          setMode={setMode}
          setAttr={setAttr}
          setExpressions={onExpressionsChanged}
          setValues={setValues}
        />
        {(isLoading || isSubmitting) && (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <DataSetSplitFallback fallback={fallback} setFallback={setFallback} />
        <div className={classes.buttons}>
          <Button variant="outlined" color="basic" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={onSubmit} disabled={isSubmitting}>
            {splitterDataSet ? 'Save' : 'Split'}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export const DataSetSplitModes = ({
  isReadOnly = false,
  highlightedId,
  mode,
  parentDataSetSchema,
  parsingErrors,
  expressions,
  attr,
  values,
  setMode,
  setAttr,
  setValues,
  setExpressions,
}: {
  isReadOnly?: boolean;
  highlightedId?: string | null;
  mode: string;
  parentDataSetSchema?: any[];
  parsingErrors?: Record<string, string>;
  attr: string;
  values: ValueItem[];
  expressions: ExpressionItem[];
  setMode?: (mode: string) => void;
  setAttr?: (attr: string | null) => void;
  setValues?: (values: ValueItem[]) => void;
  setExpressions?: (expressions: ExpressionItem[]) => void;
}) => {
  const onModeChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMode(event.target.value);
  };

  return (
    <>
      <div className={classes.modeSection}>
        <span className={classes.radioGroupLabel}>Split Nexset records based on:</span>
        {isReadOnly ? (
          <span
            className={classNames(classes.radioGroupLabel, {
              [classes.readOnlyValue]: isReadOnly,
            })}
          >
            {mode === MODE_EXPRESSIONS ? 'Conditional expressions' : 'Attribute values'}
          </span>
        ) : (
          <RadioGroup className={classes.radioGroup} value={mode} onChange={onModeChanged}>
            <FormControlLabel
              value={MODE_EXPRESSIONS}
              control={<Radio className={classes.radio} />}
              label="Conditional expressions"
            />
            <FormControlLabel
              value={MODE_VALUES}
              control={<Radio className={classes.radio} />}
              label="Attribute values"
            />
          </RadioGroup>
        )}
      </div>
      {mode === MODE_EXPRESSIONS ? (
        <ExpressionsEditor
          isReadOnly={isReadOnly}
          highlightedId={highlightedId}
          parentDataSetSchema={parentDataSetSchema}
          parsingErrors={parsingErrors}
          expressions={expressions}
          setExpressions={setExpressions}
        />
      ) : (
        <ValuesEditor
          isReadOnly={isReadOnly}
          highlightedId={highlightedId}
          parentDataSetSchema={parentDataSetSchema}
          attr={attr}
          values={values}
          setAttr={setAttr}
          setValues={setValues}
        />
      )}
    </>
  );
};

export const DataSetSplitFallback = ({
  isReadOnly = false,
  isHighlighted = false,
  fallback,
  setFallback,
}: {
  isReadOnly?: boolean;
  isHighlighted?: boolean;
  fallback: string;
  setFallback?: (fallback: string) => void;
}) => {
  const onFallbackChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFallback(event.target.value);
  };

  return (
    <div className={classes.fallbackSection}>
      <span className={classes.radioGroupLabel}>Everything else:</span>
      {isReadOnly ? (
        <span
          className={classNames(classes.radioGroupLabel, {
            [classes.readOnlyValue]: isReadOnly,
            [classes.highlighted]: isHighlighted,
          })}
        >
          {fallback === FALLBACK_NEW_NEXSET ? 'Write to a Nexset' : 'Discard'}
        </span>
      ) : (
        <RadioGroup className={classes.radioGroup} value={fallback} onChange={onFallbackChanged}>
          <FormControlLabel
            value={FALLBACK_NEW_NEXSET}
            control={<Radio className={classes.radio} />}
            label="Write to a Nexset"
          />
          <FormControlLabel
            value={FALLBACK_DISCARD}
            control={<Radio className={classes.radio} />}
            label="Discard"
          />
        </RadioGroup>
      )}
    </div>
  );
};
